<template>
  <div class="app-page commission-page" v-loading="loading">
    <div class="box-card-header"><div class="title">{{ ui.title }}</div></div>
    <div class="box-card-content">
      <div class="panel filter-panel">
        <el-tabs v-model="active" @tab-click="tabChanged">
          <el-tab-pane v-for="t in tabs" :key="t.key" :name="t.key" :label="t.label" />
        </el-tabs>
        <el-form :inline="true" size="small">
          <el-form-item v-if="active === 'trade'"><el-select v-model="query.trade_product_type" :placeholder="ui.productType" clearable style="width:150px"><el-option :label="ui.allProducts" value="" /><el-option :label="ui.futuresProduct" :value="1" /><el-option :label="ui.optionProduct" :value="2" /></el-select></el-form-item>
          <el-form-item><el-input v-model.trim="query.keyword" :placeholder="ui.keyword" clearable @keyup.enter.native="search" /></el-form-item>
          <el-form-item><el-date-picker v-model="dateRange" type="daterange" :start-placeholder="ui.startDate" :end-placeholder="ui.endDate" value-format="timestamp" /></el-form-item>
          <el-form-item><el-button type="primary" @click="search">{{ ui.search }}</el-button><el-button @click="reset">{{ ui.reset }}</el-button></el-form-item>
        </el-form>
        <div class="total">{{ ui.total }}：<b>{{ result.total_commission || '0.00' }} MEQ</b></div>
      </div>

      <div v-if="isSummary" class="panel summary-cards"><div v-for="f in summaryFields" :key="f.key"><span>{{ f.label }}</span><b>{{ (result.totals || {})[f.key] || '0.0000' }} MEQ</b></div></div>
      <div v-if="active === 'sources'" class="panel table-panel">
        <el-table :data="result.list || []" border stripe><el-table-column :label="ui.source" min-width="180"><template slot-scope="s">{{s.row.username}} ({{s.row.account_id || s.row.source_user_id}})</template></el-table-column><el-table-column v-for="f in summaryFields" :key="f.key" :prop="f.key" :label="f.label + ' (MEQ)'" min-width="145" /></el-table>
        <div class="pager"><el-pagination background layout="total, prev, pager, next" :total="Number(result.total || 0)" :page-size="query.size" :current-page="query.page" @current-change="pageChanged" /></div>
      </div>
      <div v-if="!isSummary" class="panel table-panel">
        <el-table :data="result.list || []" border stripe>
          <el-table-column type="expand">
            <template slot-scope="s">
              <div class="expand-grid">
                <Detail :label="ui.commissionOrderNo" :value="commissionOrderNo(s.row)" />
                <template v-if="active === 'trade'">
                  <Detail :label="ui.productType" :value="productName(s.row)" />
                  <Detail :label="ui.fee" :value="s.row.trade_fee" />
                  <Detail :label="ui.actualRate" :value="rateValue(s.row)" />
                  <Detail :label="ui.tradeOrderNo" :value="s.row.order_no || '-'" />
                  <Detail :label="ui.tradeNo" :value="s.row.trade_no || '-'" />
                  <Detail :label="ui.instrument" :value="s.row.trade_inst_name" />
                  <Detail :label="ui.price" :value="s.row.trade_price" />
                  <Detail :label="ui.quantity" :value="s.row.trade_vol" />
                </template>
                <template v-else-if="active === 'interest'">
                  <Detail :label="ui.interestFee" :value="s.row.trade_fee" />
                  <Detail :label="ui.actualRate" :value="rateValue(s.row)" />
                  <Detail :label="ui.tradeOrderNo" :value="s.row.order_no || '-'" />
                  <Detail :label="ui.tradeNo" :value="s.row.trade_no || '-'" />
                  <Detail :label="ui.interestBase" :value="interestValue(s.row, 'interest_base')" />
                  <Detail :label="ui.interestDays" :value="interestValue(s.row, 'day')" />
                </template>
                <template v-else-if="active === 'member'">
                  <Detail :label="ui.actualRate" :value="rateValue(s.row)" />
                  <Detail :label="ui.memberOrder" :value="s.row.order_no || memberOrderValue(s.row, 'order_no')" />
                  <Detail :label="ui.purchaseAmount" :value="memberOrderValue(s.row, 'amount')" />
                  <Detail :label="ui.memberType" :value="ui.premiumMember" />
                </template>
                <template v-else>
                  <Detail :label="ui.actualRate" :value="rateValue(s.row)" />
                  <Detail :label="ui.riskGiftAmount" :value="s.row.gift_amount" />
                  <Detail :label="ui.commissionRole" :value="s.row.commission_role_name" />
                </template>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="ui.source" min-width="170"><template slot-scope="s">{{ sourceName(s.row) }}</template></el-table-column>
          <el-table-column v-if="active === 'trade'" :label="ui.productType" min-width="110"><template slot-scope="s">{{ productName(s.row) }}</template></el-table-column>
          <el-table-column prop="commission" :label="ui.amount" min-width="130" />
          <el-table-column :label="ui.actualRate" min-width="125"><template slot-scope="s">{{ rateValue(s.row) }}</template></el-table-column>
          <el-table-column :label="ui.time" min-width="170"><template slot-scope="s">{{ formatTime(s.row.create_time || s.row.trade_deal_time) }}</template></el-table-column>
        </el-table>
        <div class="pager"><el-pagination background layout="total, prev, pager, next" :total="Number(result.total || 0)" :page-size="query.size" :current-page="query.page" @current-change="pageChanged" /></div>
      </div>
    </div>
  </div>
</template>

<script>
const Detail = {
  functional: true,
  props: ['label', 'value'],
  render (h, c) {
    const value = c.props.value === undefined || c.props.value === null || c.props.value === '' ? '-' : String(c.props.value)
    return h('div', { class: 'detail-item' }, [h('span', c.props.label), h('b', value)])
  }
}

export default {
  name: 'CommissionDetailNew',
  components: { Detail },
  data () {
    return {
      loading: false,
      active: 'trade',
      dateRange: [],
      query: { page: 1, size: 20, keyword: '', trade_product_type: '' },
      result: { list: [], total: 0, total_commission: '0.00' }
    }
  },
  computed: {
    isAgent () { const u = this.$store.state.userInfo || {}; return Number(u.identity) === 1 || Number(u.promotion_level) > 0 },
    isSummary () { return ['summary', 'sources'].indexOf(this.active) >= 0 },
    summaryFields () { return [{ key: 'futures_commission', label: this.ui.futuresProduct }, { key: 'option_commission', label: this.ui.optionProduct }, { key: 'interest_commission', label: this.ui.interest }, { key: 'membership_commission', label: this.ui.member }, { key: 'risk_gift_commission', label: this.ui.risk }, { key: 'total_commission', label: this.ui.total }] },
    tabs () { return (this.isAgent ? [{ key: 'summary', label: this.ui.summary }, { key: 'sources', label: this.ui.sources }] : []).concat([{ key: 'trade', label: this.ui.trade }, { key: 'interest', label: this.ui.interest }, { key: 'member', label: this.ui.member }, { key: 'risk_gift', label: this.ui.risk }]) },
    ui () {
      const l = String(this.$i18n.locale || '').toLowerCase()
      if (l.indexOf('ja') === 0) {
        return {
          title: 'コミッション明細',
          summary: 'コミッション集計',
          sources: '紹介元別合計',
          keyword: '紹介元氏名 / ID / 注文番号',
          startDate: '開始日',
          endDate: '終了日',
          search: '検索',
          reset: 'リセット',
          total: '累計コミッション',
          trade: '取引コミッション',
          interest: '利息コミッション',
          member: '会員コミッション',
          risk: 'リスクギフトコミッション',
          source: '紹介元',
          amount: 'コミッション金額(MEQ)',
          actualRate: '実際の差額率',
          time: '時間',
          commissionOrderNo: 'コミッション注文番号',
          tradeOrderNo: '取引注文番号',
          tradeNo: '取引番号',
          fee: '手数料(MEQ)',
          instrument: '銘柄',
          price: '取引価格',
          quantity: '数量',
          interestFee: '利息費用(MEQ)',
          interestBase: '利息計算額',
          interestDays: '利息取引日',
          memberOrder: '会員注文番号',
          purchaseAmount: '購入金額(MEQ)',
          memberType: '会員タイプ',
          premiumMember: 'プレミアム会員',
          riskGiftAmount: 'リスク補償ギフト金額(MEQ)',
          commissionRole: 'コミッション役割',
          productType: '取引商品',
          allProducts: 'すべて',
          futuresProduct: '先物',
          optionProduct: 'オプション'
        }
      }
      if (l.indexOf('en') === 0) {
        return {
          title: 'Commission Details',
          summary: 'Commission summary',
          sources: 'Totals by source user',
          keyword: 'Source name / ID / order no.',
          startDate: 'Start Date',
          endDate: 'End Date',
          search: 'Search',
          reset: 'Reset',
          total: 'Total Commission',
          trade: 'Trade Commission',
          interest: 'Interest Commission',
          member: 'Membership Commission',
          risk: 'Risk Gift Commission',
          source: 'Source',
          amount: 'Commission (MEQ)',
          actualRate: 'Actual Differential Rate',
          time: 'Time',
          commissionOrderNo: 'Commission Order No.',
          tradeOrderNo: 'Trade Order No.',
          tradeNo: 'Trade No.',
          fee: 'Fee (MEQ)',
          instrument: 'Instrument',
          price: 'Price',
          quantity: 'Quantity',
          interestFee: 'Interest Fee (MEQ)',
          interestBase: 'Interest Base',
          interestDays: 'Interest trading day',
          memberOrder: 'Membership Order',
          purchaseAmount: 'Purchase Amount (MEQ)',
          memberType: 'Membership Type',
          premiumMember: 'Premium',
          riskGiftAmount: 'Risk Gift Amount (MEQ)',
          commissionRole: 'Commission Role',
          productType: 'Product Type',
          allProducts: 'All',
          futuresProduct: 'Futures',
          optionProduct: 'Options'
        }
      }
      return {
        title: '佣金明细',
        summary: '佣金汇总',
        sources: '返佣总额（按用户）',
        keyword: '来源人姓名 / ID / 订单编号',
        startDate: '开始日期',
        endDate: '结束日期',
        search: '查询',
        reset: '重置',
        total: '累计佣金',
        trade: '交易佣金',
        interest: '利息佣金',
        member: '会员佣金',
        risk: '风险赠金佣金',
        source: '来源',
        amount: '佣金金额(MEQ)',
        actualRate: '实际返佣比例',
        time: '时间',
        commissionOrderNo: '返佣订单编号',
        tradeOrderNo: '交易订单编号',
        tradeNo: '交易编号',
        fee: '手续费(MEQ)',
        instrument: '股票名称',
        price: '交易价格',
        quantity: '数量',
        interestFee: '利息费用(MEQ)',
        interestBase: '计息金额',
        interestDays: '计息交易日',
        memberOrder: '会员订单编号',
        purchaseAmount: '会员购买金额(MEQ)',
        memberType: '会员类型',
        premiumMember: '高级会员',
        riskGiftAmount: '风险补偿赠金金额(MEQ)',
        commissionRole: '佣金角色',
        productType: '交易品类',
        allProducts: '全部品类',
        futuresProduct: '期货',
        optionProduct: '期权'
      }
    }
  },
  created () {
    const t = this.$route.query.type
    if (this.tabs.some(tab => tab.key === t)) this.active = t
    this.load()
  },
  methods: {
    load () {
      const requestId = this._commissionRequestId = (this._commissionRequestId || 0) + 1
      this.loading = true
      this.result = { list: [], total: 0, total_commission: '0.00' }
      const p = { ...this.query }
      if (this.dateRange && this.dateRange.length === 2) {
        p.start_time = Math.floor(this.dateRange[0] / 1000)
        p.end_time = Math.floor((this.dateRange[1] + 86399999) / 1000)
      }
      let req
      if (this.isSummary) req = this.$http.post('/api/promotion/commissionSummary', p)
      else if (this.active === 'trade') req = this.$http.post('/api/promotion/tradeCommissionList', p)
      else if (this.active === 'interest') req = this.$http.post('/api/promotion/interestCommissionList', p)
      else if (this.active === 'member') req = this.$http.post('/api/membership/commissionList', p)
      else req = this.$http.post('/api/promotion/riskGiftCommissionList', p)
      req.then(r => {
        if (requestId === this._commissionRequestId) this.result = r || { list: [], total: 0, total_commission: '0.00' }
      }).catch(() => { /* The shared HTTP layer displays the error. */ }).finally(() => {
        if (requestId === this._commissionRequestId) this.loading = false
      })
    },
    tabChanged () { this.query.page = 1; this.load() },
    search () { this.query.page = 1; this.load() },
    reset () { this.query = { page: 1, size: 20, keyword: '', trade_product_type: '' }; this.dateRange = []; this.load() },
    pageChanged (p) { this.query.page = p; this.load() },
    sourceName (row) {
      const u = row.source_user || row.sourceUser || row.user
      if (u) return `${u.username || '-'}${u.account_id ? ' (ID: ' + u.account_id + ')' : ''}`
      return row.account_id ? 'ID: ' + row.account_id : '-'
    },
    productName (row) { return Number(row.trade_product_type || 1) === 2 ? this.ui.optionProduct : this.ui.futuresProduct },
    commissionOrderNo (row) {
      if (row.commission_order_no) return row.commission_order_no
      if (row.commission_no) return row.commission_no
      const id = String(Number(row.id || 0)).padStart(8, '0')
      if (this.active === 'trade') return `TC${id}`
      if (this.active === 'interest') return `IC${id}`
      if (this.active === 'member') return `MC${id}`
      return `RG${id}`
    },
    rateValue (row) { return `${row.commission_delta_rate || 0}%` },
    interestValue (row, k) { return row.interest_detail && row.interest_detail[k] != null ? row.interest_detail[k] : '-' },
    memberOrderValue (row, k) { const o = row.membership_order || row.membershipOrder; return o && o[k] !== undefined ? o[k] : '-' },
    formatTime (v) {
      if (!v) return '-'
      if (typeof v === 'string' && v.indexOf('-') > -1) return v
      const d = new Date(Number(v) * 1000)
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    }
  }
}
</script>

<style scoped lang="scss">
.summary-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:18px}.summary-cards b{display:block;color:#00c8f4;margin-top:10px}
.commission-page {
  min-height: calc(100vh - 90px);
  margin: 0 -38px;
  padding: 0 38px 38px;
  color: #d7edf4;
  background: #061019;
  .box-card-header { padding-top: 18px; margin-bottom: 14px; }
  .title { color: #00c8f4; font-size: 18px; font-weight: 600; }
  .panel { border: 1px solid rgba(31,146,176,.35); background: linear-gradient(180deg, rgba(8,33,45,.9), rgba(4,20,29,.94)); padding: 16px; border-radius: 7px; margin-bottom: 15px; }
  .total { color: #8ba1af; font-size: 12px; }
  .total b { color: #00c8f4; }
  .pager { text-align: right; margin-top: 15px; }
  .expand-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px 30px; padding: 12px 26px; background: #071923; }
  .detail-item { display: flex; justify-content: space-between; border-bottom: 1px solid rgba(74,106,124,.35); padding: 8px 0; }
  .detail-item span { color: #829aa8; }
  .detail-item b { color: #dce8ee; font-weight: 500; }
}

::v-deep {
  .el-tabs__item { color: #7892a1; }
  .el-tabs__item.is-active { color: #00c8f4; }
  .el-tabs__active-bar { background: #00c8f4; }
  .el-tabs__nav-wrap::after { background: rgba(63, 96, 114, .35); }
  .el-input__inner, .el-range-editor.el-input__inner { color: #d7edf4; background: #0a2635; border-color: #31546a; }
  .el-range-input { color: #d7edf4; background: transparent; }
  .el-range-separator, .el-input__icon { color: #819aa9; }
  .el-button--default { color: #c5d7e0; background: #0a2635; border-color: #31546a; }
  .el-table, .el-table__expanded-cell { color: #d7edf4; background: transparent; }
  .el-table th, .el-table tr, .el-table td { background: #08212d !important; border-color: rgba(65, 104, 124, .38) !important; }
  .el-table th { color: #8fa8b6; }
  .el-table--striped .el-table__body tr.el-table__row--striped td { background: #0a2734 !important; }
  .el-table__body tr:hover > td { background: #0d3040 !important; }
  .el-table::before, .el-table--border::after, .el-table--group::after { background: rgba(65, 104, 124, .38); }
  .el-table__expand-icon { color: #78a5ba; }
  .el-pagination__total { color: #8399a6; }
}
</style>
